import './PaymentForm.css';
import React, { useEffect, useState, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PaymentFrame from 'Features/Payment/PaymentFrame';
import SecureCheckoutLabel from 'Features/Payment/SecureCheckoutLabel';
import { SubmitError } from 'Components/Fields/SubmitError';
import { PurchaseConfirmation } from 'Features/Payment/PurchaseConfirmation';
import { SubmitButton, CancelButton } from 'Components/Fields/Buttons';
import { resetTax } from 'State/Checkout/CheckoutActions';
import { useForm } from 'Hooks/useForm';
import useVisualViewport from 'Hooks/useVisibleViewport';

const EXPIRATION_ERROR =
  "There's been an error in processing your payment. Please review and enter the correct Payment Method information.";

export const PaymentForm = forwardRef(
  (
    {
      editForm = false,
      onFormSubmit,
      submitButtonText,
      onFormCompletedTrackingFields,
      showCancelButton,
      onCancelClick,
      showSecureCheckoutLabel = false,
      updatePaymentServiceId,
      selectedPlan,
      billingAddress = {},
      setEnablePaymentButton,
      setIsSubmitting,
      setErrorMessage,
    },
    ref
  ) => {
    const formName = editForm ? 'editPaymentForm' : 'paymentForm';
    const { submitting, formError, handleSubmit, resetFormError } = useForm({
      onFormSubmit,
      formName,
      onFormCompletedTrackingFields,
      alwaysAllowSubmit: true, //logic for blocking submit is external to useForm via enableSubmit useState below
    });

    const dispatch = useDispatch();
    const { state = '' } = billingAddress;
    const [isPurchaseConfirmed, setPurchaseConfirmation] = useState(false);
    const [isPaymentFormValid, setPaymentFormValid] = useState(false);
    const [enableSubmit, setEnableSubmit] = useState(false);
    const [expiryError, setExpiryError] = useState(false);
    const errorMessage = expiryError ? EXPIRATION_ERROR : formError;
    const cardPaymentForm = useRef();
    const { isObstructed } = useVisualViewport();

    const showPurchaseConfirm = useMemo(
      () => state === 'VERMONT' && selectedPlan.period === 'NoOfYears',
      [state, selectedPlan]
    );

    // Ref Object giving PaymentPage component access to form submission
    const formSubmitRef = {
      submitForm: () => {
        cardPaymentForm.current.requestSubmit();
      },
    };

    useEffect(() => {
      dispatch(resetTax());

      // need to reset billing address so that when we return to the saved card info form
      //    it doesn't default to what the user entered here
      return () => {
        dispatch(resetTax());
      };
    }, [dispatch]);

    //added a check for formErrorId because errorMessage is giving back a truthy empty object
    useEffect(() => {
      if (errorMessage && errorMessage.formErrorId) {
        if (!editForm) setIsSubmitting(false);
        setEnableSubmit(false);
      }
    }, [errorMessage, setIsSubmitting, editForm]);

    useEffect(() => {
      if (showPurchaseConfirm) {
        setEnableSubmit(isPurchaseConfirmed && isPaymentFormValid);
        if (!editForm) {
          setEnablePaymentButton(isPurchaseConfirmed && isPaymentFormValid);
        }
      } else {
        setPurchaseConfirmation(false);
        setEnableSubmit(isPaymentFormValid);
        if (!editForm) {
          setEnablePaymentButton(isPaymentFormValid);
        }
      }
    }, [showPurchaseConfirm, isPurchaseConfirmed, isPaymentFormValid, editForm, setEnablePaymentButton]);

    useImperativeHandle(ref, () => formSubmitRef);

    const handleClickCancel = (e) => {
      setEnableSubmit(false);
      if (setEnablePaymentButton) {
        setEnablePaymentButton(false);
      }
      onCancelClick(e);
    };

    return (
      <div className="cardPayment">
        {showSecureCheckoutLabel && <SecureCheckoutLabel />}
        <PaymentFrame
          formName={formName}
          onValidate={setPaymentFormValid}
          onExpiryError={setExpiryError}
          updatePaymentServiceId={updatePaymentServiceId}
          resetFormError={resetFormError}
          setErrorMessage={setErrorMessage}
        />
        <form ref={cardPaymentForm} className="paymentForm" onSubmit={(event) => handleSubmit(event)}>
          <PurchaseConfirmation
            show={showPurchaseConfirm}
            ctaText={submitButtonText}
            selectedPlan={selectedPlan}
            handleChange={setPurchaseConfirmation}
          />
          <SubmitError error={errorMessage} formName={formName} trackError={false} />
          {/* Submit button for cards when in edit mode; edit mode has own screen and need the button under form */}
          {editForm && (
            <SubmitButton
              text={submitButtonText}
              submitting={submitting}
              isDisabled={!enableSubmit}
              isObstructed={isObstructed}
              modernLayout
            />
          )}
          {showCancelButton && <CancelButton text="Cancel" onClick={handleClickCancel} />}
        </form>
      </div>
    );
  }
);
